import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { InstallButton } from '@/context/InstallContext';

export default function OrganizationDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  const [organization, setOrganization] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [vehicleDialogOpen, setVehicleDialogOpen] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  
  const [vehicleForm, setVehicleForm] = useState({
    make: '', model: '', year: '', license_plate: '', vin: '', current_mileage: '', condition: 'good', notes: ''
  });
  
  const [contactForm, setContactForm] = useState({
    name: '', email: '', phone: '', role: 'driver'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [orgRes, vehiclesRes, contactsRes, remindersRes] = await Promise.all([
        axios.get(`${API}/organization/me`),
        axios.get(`${API}/organization/vehicles`),
        axios.get(`${API}/organization/contacts`),
        axios.get(`${API}/organization/service-reminders`)
      ]);
      setOrganization(orgRes.data);
      setVehicles(vehiclesRes.data);
      setContacts(contactsRes.data);
      setReminders(remindersRes.data);
    } catch (error) {
      toast.error('Failed to load data');
      if (error.response?.status === 403) {
        logout();
        navigate('/organization/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const addVehicle = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...vehicleForm,
        year: parseInt(vehicleForm.year),
        current_mileage: vehicleForm.current_mileage ? parseInt(vehicleForm.current_mileage) : null
      };
      await axios.post(`${API}/organization/vehicles`, payload);
      toast.success('Vehicle added successfully');
      setVehicleDialogOpen(false);
      setVehicleForm({ make: '', model: '', year: '', license_plate: '', vin: '', current_mileage: '', condition: 'good', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add vehicle');
    }
  };

  const deleteVehicle = async (vehicleId) => {
    if (!confirm('Are you sure you want to remove this vehicle?')) return;
    try {
      await axios.delete(`${API}/organization/vehicles/${vehicleId}`);
      toast.success('Vehicle removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove vehicle');
    }
  };

  const addContact = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/organization/contacts`, contactForm);
      toast.success('Contact added successfully');
      setContactDialogOpen(false);
      setContactForm({ name: '', email: '', phone: '', role: 'driver' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add contact');
    }
  };

  const deleteContact = async (contactId) => {
    if (!confirm('Are you sure you want to remove this contact?')) return;
    try {
      await axios.delete(`${API}/organization/contacts/${contactId}`);
      toast.success('Contact removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove contact');
    }
  };

  const getConditionColor = (condition) => {
    switch (condition) {
      case 'excellent': return 'bg-green-100 text-green-800';
      case 'good': return 'bg-blue-100 text-blue-800';
      case 'fair': return 'bg-yellow-100 text-yellow-800';
      case 'needs_attention': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getServiceStatusColor = (status) => {
    switch (status) {
      case 'overdue': return 'bg-red-100 text-red-800 border-red-300';
      case 'due_soon': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'ok': return 'bg-green-100 text-green-800 border-green-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-xl font-heading text-foreground">Loading...</div>
      </div>
    );
  }

  const overdueVehicles = vehicles.filter(v => v.service_status === 'overdue');
  const dueSoonVehicles = vehicles.filter(v => v.service_status === 'due_soon');

  return (
    <div className="min-h-screen bg-background" data-testid="organization-dashboard">
      <nav className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_vehicle-maintenance-21/artifacts/76wdsgvy_WhatsApp%20Image%202025-12-16%20at%2017.28.15.jpeg" 
                alt="Logo" 
                className="h-12 w-12 object-contain"
              />
              <div>
                <span className="text-xl font-heading font-bold text-foreground block">EXCEL ERA</span>
                <span className="text-xs text-primary font-bold uppercase tracking-wide">Fleet Management</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <InstallButton variant="default" size="sm" />
              <span className="text-sm font-medium text-muted-foreground hidden md:inline">{organization?.name}</span>
              <Button variant="ghost" onClick={logout} data-testid="logout-btn" className="font-bold uppercase text-sm">
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Welcome Banner */}
        <div className="bg-primary text-white p-6 rounded-sm shadow-hard mb-8">
          <h1 className="text-2xl font-heading font-bold mb-1">{organization?.name}</h1>
          <p className="text-sm opacity-90">{organization?.organization_type?.toUpperCase()} • {organization?.phone}</p>
          <p className="text-sm opacity-75 mt-1">Contact: {organization?.contact_person_name} ({organization?.contact_person_email})</p>
        </div>

        {/* Service Reminders Alert */}
        {reminders.length > 0 && (
          <div className="bg-red-50 border-2 border-red-200 p-4 rounded-sm mb-8">
            <h2 className="font-bold text-red-800 mb-2 flex items-center gap-2">
              ⚠️ Service Reminders ({reminders.length} vehicles need attention)
            </h2>
            <div className="space-y-2">
              {reminders.slice(0, 3).map((r, i) => (
                <div key={i} className="flex justify-between items-center bg-white p-2 rounded">
                  <span className="font-medium">{r.vehicle_info} ({r.license_plate})</span>
                  <span className={`text-xs font-bold px-2 py-1 rounded ${r.status === 'overdue' ? 'bg-red-600 text-white' : 'bg-orange-500 text-white'}`}>
                    {r.days_until_service < 0 ? `${Math.abs(r.days_until_service)} days overdue` : `${r.days_until_service} days left`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white border-2 border-border p-6 rounded-sm">
            <div className="text-4xl font-heading font-black text-primary mb-2">{vehicles.length}</div>
            <div className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Total Vehicles</div>
          </div>
          <div className="bg-white border-2 border-border p-6 rounded-sm">
            <div className="text-4xl font-heading font-black text-red-600 mb-2">{overdueVehicles.length}</div>
            <div className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Service Overdue</div>
          </div>
          <div className="bg-white border-2 border-border p-6 rounded-sm">
            <div className="text-4xl font-heading font-black text-orange-500 mb-2">{dueSoonVehicles.length}</div>
            <div className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Due Soon</div>
          </div>
          <div className="bg-white border-2 border-border p-6 rounded-sm">
            <div className="text-4xl font-heading font-black text-blue-600 mb-2">{contacts.length}</div>
            <div className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Contact Persons</div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="vehicles" className="space-y-6">
          <TabsList className="bg-white border-2 border-border p-1">
            <TabsTrigger value="vehicles" className="font-bold uppercase text-sm" data-testid="vehicles-tab">Vehicles</TabsTrigger>
            <TabsTrigger value="contacts" className="font-bold uppercase text-sm" data-testid="contacts-tab">Contacts</TabsTrigger>
          </TabsList>

          {/* Vehicles Tab */}
          <TabsContent value="vehicles" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-heading font-bold text-foreground">Fleet Vehicles</h2>
              <Dialog open={vehicleDialogOpen} onOpenChange={setVehicleDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="add-vehicle-btn">+ Add Vehicle</Button>
                </DialogTrigger>
                <DialogContent className="bg-white">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-heading font-bold">Add Vehicle to Fleet</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={addVehicle} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="font-bold uppercase text-sm">Make *</Label>
                        <Input required className="input-field mt-1" placeholder="Toyota" value={vehicleForm.make} onChange={(e) => setVehicleForm({ ...vehicleForm, make: e.target.value })} />
                      </div>
                      <div>
                        <Label className="font-bold uppercase text-sm">Model *</Label>
                        <Input required className="input-field mt-1" placeholder="Camry" value={vehicleForm.model} onChange={(e) => setVehicleForm({ ...vehicleForm, model: e.target.value })} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="font-bold uppercase text-sm">Year *</Label>
                        <Input required type="number" className="input-field mt-1" placeholder="2023" value={vehicleForm.year} onChange={(e) => setVehicleForm({ ...vehicleForm, year: e.target.value })} />
                      </div>
                      <div>
                        <Label className="font-bold uppercase text-sm">License Plate *</Label>
                        <Input required className="input-field mt-1" placeholder="ABC-123-XY" value={vehicleForm.license_plate} onChange={(e) => setVehicleForm({ ...vehicleForm, license_plate: e.target.value })} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="font-bold uppercase text-sm">VIN</Label>
                        <Input className="input-field mt-1" placeholder="Optional" value={vehicleForm.vin} onChange={(e) => setVehicleForm({ ...vehicleForm, vin: e.target.value })} />
                      </div>
                      <div>
                        <Label className="font-bold uppercase text-sm">Current Mileage (km)</Label>
                        <Input type="number" className="input-field mt-1" placeholder="50000" value={vehicleForm.current_mileage} onChange={(e) => setVehicleForm({ ...vehicleForm, current_mileage: e.target.value })} />
                      </div>
                    </div>
                    <div>
                      <Label className="font-bold uppercase text-sm">Condition</Label>
                      <Select value={vehicleForm.condition} onValueChange={(v) => setVehicleForm({ ...vehicleForm, condition: v })}>
                        <SelectTrigger className="input-field mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="excellent">Excellent</SelectItem>
                          <SelectItem value="good">Good</SelectItem>
                          <SelectItem value="fair">Fair</SelectItem>
                          <SelectItem value="needs_attention">Needs Attention</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="font-bold uppercase text-sm">Notes</Label>
                      <Input className="input-field mt-1" placeholder="Any notes about this vehicle" value={vehicleForm.notes} onChange={(e) => setVehicleForm({ ...vehicleForm, notes: e.target.value })} />
                    </div>
                    <Button type="submit" className="btn-primary w-full h-12">Add Vehicle</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {vehicles.length === 0 ? (
              <div className="bg-white border-2 border-dashed border-border p-12 rounded-sm text-center">
                <p className="text-xl font-medium text-muted-foreground">No vehicles in your fleet</p>
                <p className="text-sm text-muted-foreground mt-2">Add your first vehicle to get started</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {vehicles.map(vehicle => (
                  <div key={vehicle.id} className={`bg-white border-2 p-4 rounded-sm ${getServiceStatusColor(vehicle.service_status)}`} data-testid="vehicle-card">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-heading font-bold text-lg">{vehicle.make} {vehicle.model}</h3>
                        <p className="text-sm text-muted-foreground">{vehicle.year} • {vehicle.license_plate}</p>
                      </div>
                      <span className={`text-xs font-bold px-2 py-1 rounded uppercase ${getConditionColor(vehicle.condition)}`}>
                        {vehicle.condition?.replace('_', ' ')}
                      </span>
                    </div>
                    
                    {vehicle.current_mileage && (
                      <p className="text-sm"><span className="text-muted-foreground">Mileage:</span> {vehicle.current_mileage.toLocaleString()} km</p>
                    )}
                    
                    {vehicle.next_service_date && (
                      <div className="mt-2 p-2 bg-white/50 rounded">
                        <p className="text-xs text-muted-foreground">Next Service:</p>
                        <p className="font-bold text-sm">{vehicle.next_service_date}</p>
                        {vehicle.days_until_service !== undefined && (
                          <p className={`text-xs font-bold ${vehicle.days_until_service < 0 ? 'text-red-600' : vehicle.days_until_service <= 7 ? 'text-orange-600' : 'text-green-600'}`}>
                            {vehicle.days_until_service < 0 ? `${Math.abs(vehicle.days_until_service)} days overdue` : `${vehicle.days_until_service} days left`}
                          </p>
                        )}
                      </div>
                    )}
                    
                    {vehicle.notes && (
                      <p className="text-xs text-muted-foreground mt-2 italic">{vehicle.notes}</p>
                    )}
                    
                    <Button variant="ghost" size="sm" className="mt-3 text-red-600 hover:bg-red-50 w-full" onClick={() => deleteVehicle(vehicle.id)}>
                      Remove Vehicle
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-heading font-bold text-foreground">Contact Persons</h2>
              <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="add-contact-btn">+ Add Contact</Button>
                </DialogTrigger>
                <DialogContent className="bg-white">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-heading font-bold">Add Contact Person</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={addContact} className="space-y-4">
                    <div>
                      <Label className="font-bold uppercase text-sm">Name *</Label>
                      <Input required className="input-field mt-1" placeholder="Full Name" value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} />
                    </div>
                    <div>
                      <Label className="font-bold uppercase text-sm">Email *</Label>
                      <Input required type="email" className="input-field mt-1" placeholder="email@company.com" value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} />
                    </div>
                    <div>
                      <Label className="font-bold uppercase text-sm">Phone *</Label>
                      <Input required className="input-field mt-1" placeholder="+234 XXX XXX XXXX" value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} />
                    </div>
                    <div>
                      <Label className="font-bold uppercase text-sm">Role</Label>
                      <Select value={contactForm.role} onValueChange={(v) => setContactForm({ ...contactForm, role: v })}>
                        <SelectTrigger className="input-field mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fleet_manager">Fleet Manager</SelectItem>
                          <SelectItem value="driver">Driver</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button type="submit" className="btn-primary w-full h-12">Add Contact</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {contacts.length === 0 ? (
              <div className="bg-white border-2 border-dashed border-border p-12 rounded-sm text-center">
                <p className="text-xl font-medium text-muted-foreground">No contact persons added</p>
                <p className="text-sm text-muted-foreground mt-2">Add contacts to manage your fleet</p>
              </div>
            ) : (
              <div className="bg-white border-2 border-border rounded-sm overflow-hidden">
                <table className="w-full">
                  <thead className="bg-secondary text-white">
                    <tr>
                      <th className="px-6 py-4 text-left font-bold uppercase text-sm">Name</th>
                      <th className="px-6 py-4 text-left font-bold uppercase text-sm">Email</th>
                      <th className="px-6 py-4 text-left font-bold uppercase text-sm">Phone</th>
                      <th className="px-6 py-4 text-left font-bold uppercase text-sm">Role</th>
                      <th className="px-6 py-4 text-left font-bold uppercase text-sm">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {contacts.map(contact => (
                      <tr key={contact.id} className="hover:bg-accent transition-colors">
                        <td className="px-6 py-4 font-bold">{contact.name}</td>
                        <td className="px-6 py-4">{contact.email}</td>
                        <td className="px-6 py-4">{contact.phone}</td>
                        <td className="px-6 py-4">
                          <span className="text-xs font-bold px-2 py-1 rounded bg-blue-100 text-blue-800 uppercase">
                            {contact.role?.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => deleteContact(contact.id)}>
                            Remove
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
